import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ChevronDown, ChevronUp, Pencil, Trash2 } from "lucide-react";
import type { ScorecardCategory, ScorecardField } from "@/types/scorecard";

interface CategoryManagerProps {
	categories: ScorecardCategory[];
	fields: ScorecardField[];
	onEdit: (category: ScorecardCategory) => void;
	onDelete: (category: ScorecardCategory) => void;
	onMove: (category: ScorecardCategory, direction: -1 | 1) => void;
}

/** Ordered list of scorecard categories with move/edit/delete controls. */
export const CategoryManager: React.FC<CategoryManagerProps> = ({
	categories,
	fields,
	onEdit,
	onDelete,
	onMove,
}) => {
	const { t } = useTranslation();

	if (categories.length === 0) return null;

	return (
		<div className="space-y-2">
			<h3 className="text-sm font-medium text-foreground">
				{t("admin.scorecardFields.categories.title", "Categories")}
			</h3>
			<div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
				{categories.map((category, index) => {
					const fieldCount = fields.filter((field) => field.category_id === category.id).length;
					return (
						<div
							key={category.id}
							className="flex items-center justify-between gap-2 rounded-sm border border-border bg-card p-2.5 hover:bg-muted/60"
						>
							<div className="flex min-w-0 items-center gap-2.5">
								<span
									className="h-3 w-3 shrink-0 rounded-full"
									style={{ backgroundColor: category.color ?? undefined }}
									aria-hidden="true"
								/>
								<div className="min-w-0">
									<div className="truncate text-sm font-medium text-foreground">{category.name}</div>
									<div className="truncate text-xs text-muted-foreground">
										{t("admin.scorecardFields.categories.fieldCount", "{{count}} field(s)", {
											count: fieldCount,
										})}
										{category.description ? ` · ${category.description}` : ""}
									</div>
								</div>
							</div>
							<div className="flex shrink-0 items-center">
								<Tooltip>
									<TooltipTrigger asChild>
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											disabled={index === 0}
											onClick={() => onMove(category, -1)}
											aria-label={t("admin.scorecardFields.moveUp", "Move up")}
										>
											<ChevronUp className="h-3.5 w-3.5" />
										</Button>
									</TooltipTrigger>
									<TooltipContent>{t("admin.scorecardFields.moveUp", "Move up")}</TooltipContent>
								</Tooltip>
								<Tooltip>
									<TooltipTrigger asChild>
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											disabled={index === categories.length - 1}
											onClick={() => onMove(category, 1)}
											aria-label={t("admin.scorecardFields.moveDown", "Move down")}
										>
											<ChevronDown className="h-3.5 w-3.5" />
										</Button>
									</TooltipTrigger>
									<TooltipContent>{t("admin.scorecardFields.moveDown", "Move down")}</TooltipContent>
								</Tooltip>
								<Tooltip>
									<TooltipTrigger asChild>
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											onClick={() => onEdit(category)}
											aria-label={t("admin.scorecardFields.categories.edit", "Edit category")}
										>
											<Pencil className="h-3.5 w-3.5" />
										</Button>
									</TooltipTrigger>
									<TooltipContent>
										{t("admin.scorecardFields.categories.edit", "Edit category")}
									</TooltipContent>
								</Tooltip>
								<AlertDialog>
									<Tooltip>
										<TooltipTrigger asChild>
											<AlertDialogTrigger asChild>
												<Button
													variant="ghost"
													size="icon"
													className="h-7 w-7 text-muted-foreground hover:text-destructive"
													aria-label={t("admin.scorecardFields.categories.delete", "Delete category")}
												>
													<Trash2 className="h-3.5 w-3.5" />
												</Button>
											</AlertDialogTrigger>
										</TooltipTrigger>
										<TooltipContent>
											{t("admin.scorecardFields.categories.delete", "Delete category")}
										</TooltipContent>
									</Tooltip>
									<AlertDialogContent>
										<AlertDialogHeader>
											<AlertDialogTitle>
												{t("admin.scorecardFields.categories.delete", "Delete category")}
											</AlertDialogTitle>
											<AlertDialogDescription>
												{t(
													"admin.scorecardFields.categories.deleteConfirm",
													'Delete "{{name}}"? Its fields are kept and become uncategorized.',
													{ name: category.name },
												)}
											</AlertDialogDescription>
										</AlertDialogHeader>
										<AlertDialogFooter>
											<AlertDialogCancel>{t("common.cancel", "Cancel")}</AlertDialogCancel>
											<AlertDialogAction
												onClick={() => onDelete(category)}
												className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
											>
												{t("common.delete", "Delete")}
											</AlertDialogAction>
										</AlertDialogFooter>
									</AlertDialogContent>
								</AlertDialog>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};
